import React from "react";

// Live documentation cheat-sheet under the Today's Visit draft, one block per section.
// It mirrors the record's own mandatory fields and the claim rules — the same things the
// EMR score and the EMR Check will ask for later — so a note dictated against this list
// comes out complete on the first pass (2026-08-17 review: "it will be the same, just
// listed here"). Content is seeded from cpt-recommender/docs/good-medical-record.md and
// is PENDING Dr. Nadine's standards baseline (task BE-5).
//
// The list checks itself off while the clinician writes, on two levels of confidence:
//   mentioned — a conservative keyword match saw the topic in the draft text
//   captured  — /emr/compose actually extracted a value for it (after Generate)
// Matchers err on the side of NOT ticking: a wrongly ticked item makes an incomplete
// note read as done, which is the same false-confidence trap as the old 100/100 score.
// Items with neither `match` nor `fields` are plain advice and carry no checkbox.

const GUIDELINES = {
    subjective: {
        title: "Subjective — what the patient reports",
        items: [
            {
                id: "cc", label: "Chief complaint: site + symptom, in the patient's words",
                match: /\b(pain|ache|aching|stiff(ness)?|numb(ness)?|tingling|weak(ness)?|swelling|sore(ness)?|injur)\w*/i,
                fields: ["subjective.complaint.complaint"],
            },
            {
                id: "onset", label: "Onset & mechanism — how it started; name any trauma",
                match: /\b(onset|started|began|since|gradual(ly)?|sudden(ly)?|insidious|trauma|fall|accident|lifting|twist)\w*/i,
                fields: ["subjective.complaint.onset", "subjective.complaint.historyOfTrauma",
                         "subjective.complaint.etiology"],
            },
            {
                id: "duration", label: "Duration — enough to call it acute / subacute / chronic",
                match: /\b(\d+\s*(day|week|month|year)s?|acute|subacute|chronic|yesterday|last\s+(week|month))\b/i,
                fields: ["subjective.complaint.durationType"],
            },
            {
                id: "pain", label: "Pain score 0–10 (VAS)",
                match: /(\d{1,2}\s*\/\s*10|\bVAS\b|pain\s+(score|level|severity))/i,
                fields: ["subjective.complaint.pain"],
            },
            {
                id: "character", label: "Character, aggravating and relieving factors",
                match: /\b(dull|sharp|burning|throbbing|shooting|aggravat|worse\s+(on|with|when|at)|reliev|eases?|better\s+(with|when|on))\w*/i,
                fields: ["subjective.complaint.character", "subjective.complaint.aggravatingFactor",
                         "subjective.complaint.relievingFactors"],
            },
            {
                id: "redflags", label: "Red flags — screen and say \"no red flags\" explicitly",
                match: /\b(red\s+flags?|night\s+pain|fever|weight\s+loss|night\s+sweats|bladder|bowel|dizziness)\b/i,
                fields: ["subjective.complaint.redFlag", "subjective.complaint.redFlagNormal"],
            },
            {
                id: "history", label: "History & allergies — negatives count (\"no known allergies\")",
                match: /\b(allerg\w*|NKDA|medical\s+history|diabet\w*|hypertension|no\s+significant\s+(past\s+)?(medical\s+)?history)\b/i,
                fields: ["subjective.allergy.none", "subjective.medicalHistory.none"],
            },
            {
                id: "followup", label: "Follow-up visits: response since last visit (better / same / worse)",
                match: /\b(improv\w*|worsen\w*|no\s+change|(better|worse|same)\s+(than|since|as)|since\s+last\s+(visit|session))\b/i,
                fields: ["subjective.complaint.nature"],
            },
        ],
    },
    objective: {
        title: "Objective — what you found",
        items: [
            {id: "specific", label: "Be specific — a bare \"abnormal\" is never enough"},
            {
                id: "rom", label: "ROM: joint, movement, degrees, side",
                match: /\b(ROM|range\s+of\s+motion|flexion|extension|abduction|adduction|rotation|\d{1,3}\s*(°|degrees?))\b/i,
            },
            {
                id: "tests", label: "Special tests: name + result",
                match: /\b(SLR|straight\s+leg\s+raise|Lachman|McMurray|Neer|Hawkins|Spurling|special\s+tests?)\b/i,
            },
            {
                id: "exam", label: "Palpation, posture, gait — state \"normal\" explicitly when checked",
                match: /\b(palpation|tender(ness)?|posture|gait|observation|inspection)\b/i,
                fields: ["assessment.palpation.none", "assessment.posture.none",
                         "assessment.gaitAnalysis.none", "assessment.observation.none"],
            },
            {
                id: "vitals", label: "Vitals if taken (BP as 120/80, temperature, pulse)",
                match: /\b(\d{2,3}\s*\/\s*\d{2,3}|BP|blood\s+pressure|pulse|heart\s+rate|temperature|respiration)\b/i,
                fields: ["assessment.vitalSigns.bloodPressureAbove", "assessment.vitalSigns.pulse",
                         "assessment.vitalSigns.temperature", "assessment.vitalSigns.respiration",
                         "assessment.vitalSigns.height", "assessment.vitalSigns.weight"],
            },
            {id: "negatives", label: "Pertinent negatives for the affected region"},
        ],
    },
    treatment: {
        title: "Treatment & plan",
        items: [
            {
                id: "done", label: "What was done today: modality + area + dosage/time",
                match: /\b(manual\s+therapy|mobilis?zation|manipulation|ultrasound|TENS|dry\s+needling|massage|stretch\w*|strengthen\w*|exercises?|performed|treated\s+with)\b/i,
            },
            {
                id: "response", label: "Response to today's treatment",
                match: /\b(tolerated|responded|post[-\s]treatment|felt\s+(better|worse)\s+after)\b/i,
                fields: ["treatment.response.overallImproveValue", "treatment.response.romImproveValue",
                         "treatment.response.painScoreValue"],
            },
            {
                id: "plan", label: "Plan: frequency & duration + a measurable goal",
                match: /\b(\d+\s*x\s*\/?\s*(week|wk)|per\s+week|for\s+\d+\s+weeks?|goal|plan)\b/i,
                fields: ["treatment.goal.details"],
            },
            {
                id: "sessions", label: "Number of sessions recommended",
                match: /\b\d+\s+sessions?\b/i,
                fields: ["treatment.treatmentSchedule.numberOfRecommendedSessions"],
            },
            {
                id: "education", label: "Patient education given; consent where relevant",
                match: /\b(educat\w*|advised|consent|explained|home\s+(exercise|program)|HEP)\b/i,
            },
            {id: "times", label: "Timed codes need the visit start/end times on record"},
        ],
    },
};

// mentioned | captured | null. Pure so it can be unit-tested; regexes carry no /g flag,
// so .test() is stateless.
export const itemCoverage = (item, text, composedPaths) => {
    if (item.fields && item.fields.some(path => composedPaths.has(path))) return "captured";
    if (item.match && text && item.match.test(text)) return "mentioned";
    return null;
};

const COVERED_TITLE = {
    captured: "Captured — the parse extracted a value for this",
    mentioned: "Mentioned in the note — will be confirmed on Generate",
};

export default function GuidelinePanel({section, text, composedPaths}) {
    const guide = GUIDELINES[section] || GUIDELINES.subjective;
    const composed = composedPaths instanceof Set ? composedPaths : new Set(composedPaths || []);

    const rows = guide.items.map(item => ({item, state: itemCoverage(item, text, composed)}));
    const checkable = rows.filter(({item}) => item.match || item.fields);
    const covered = checkable.filter(({state}) => state).length;

    return (
        <div className="emr-check-guide">
            <div className="emr-check-guide-title">
                {guide.title}
                {checkable.length > 0 &&
                    <span className="emr-check-guide-count">{covered}/{checkable.length} covered</span>}
            </div>
            {/* One item per line; a covered item turns green and strikes through — the
                strike is the tick, no checkbox column needed. */}
            <ul className="emr-check-guide-list">
                {rows.map(({item, state}) =>
                    <li key={item.id}
                        className={state ? `covered ${state}` : (item.match || item.fields ? "" : "advice")}
                        title={state ? COVERED_TITLE[state] : undefined}>
                        {item.label}
                    </li>)}
            </ul>
            <div className="emr-check-guide-foot">
                Covers the record's mandatory fields — dictate against it and the EMR
                score lands at 100.
            </div>
        </div>
    );
}
